import React, { useState, useEffect, useMemo, useCallback } from "react";
import { EntityTablePage, DeleteConfirmationConfig } from "@/components/EntityTablePage";
import { getProjectTableConfig, MarketOption } from "@/config/tableConfigs/projectTableConfig";
import { projectService } from "@/services/projectService";
import { ProjectType } from "@/types/db/projects";
import useAuth from "@/hooks/useAuth";
import { NoPermissionScreen } from "@/components/screens/shared/NoPermissionScreen";
import { FromDB } from "@/types";
import { supabase } from "@/lib/supabase-client";
import { DeleteProjectModal } from "@/components/Modals/DeleteProjectModal";
import { ExportProjectModal } from "@/components/Modals/ExportProjectModal";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useSetPageTitle } from "@/hooks/useSetPageTitle";
import { useModal } from "@/hooks/useModal";
import useToast from "@/hooks/useToast";
import { convertToCSV, downloadCSV } from "@/lib/csvUtils";

export default function ProjectsPage() {
  const { userRoles, displayNames } = useAuth();
  const toast = useToast();
  const { openModal, closeModal } = useModal();
  const [marketOptions, setMarketOptions] = useState<MarketOption[]>([]);
  const [showArchived, setShowArchived] = useState(false);

  useSetPageTitle(displayNames?.projects?.plural);

  // Fetch markets on mount
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .schema("lists")
        .from("markets")
        .select("id, name")
        .order("name", { ascending: true });

      if (data) {
        setMarketOptions(data.map((m) => ({ value: m.id, label: m.name })));
      }
    })();
  }, []);

  const tableConfig = useMemo(
    () => getProjectTableConfig(userRoles, marketOptions, displayNames),
    [userRoles, marketOptions, displayNames],
  );

  // Check if user has projects read permission
  const hasProjectsAccess = useMemo(() => {
    return userRoles?.some((r) => r.permissions?.projects?.read === true) ?? false;
  }, [userRoles]);

  const buildProjectId = (project: any): string => {
    const created = project.created_at ? new Date(project.created_at) : new Date();
    const yy = String(created.getFullYear()).slice(-2);
    const shortUuid = (project.id || "").replace(/-/g, "").slice(0, 8).toUpperCase();
    return `${yy}QS${shortUuid}`;
  };

  const formatSageDate = (isoString: string | null): string => {
    if (!isoString) return "";
    const d = new Date(isoString);
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  };

  const runSageExport = useCallback(
    async ({ selectedIds }: any) => {
      const freshData = await projectService.getProjects();
      const selectedSet = new Set(selectedIds);
      const selected = freshData.filter((p: any) => selectedSet.has(p.id));

      const sageData = selected.map((project: any) => ({
        DONOTIMPORT: "",
        PROJECT_ID: buildProjectId(project),
        PROJECT_NAME: project.name || "",
        PROJECT_CATEGORY: "Contract",
        PROJECT_DESCRIPTION: project.name || "",
        CUSTOMER_ID: project.client?.meta_data?.sage_id || "",
        BEGIN_DATE: formatSageDate(project.start_time),
        END_DATE: formatSageDate(project.end_time),
        DEPARTMENT_ID: "QSR",
        LOCATION_ID: "3017",
        CURRENCY: "USD",
        EVENT_NAME: project.name || "",
        STATE: project.location?.state || "",
      }));

      const columns = [
        "DONOTIMPORT",
        "PROJECT_ID",
        "PROJECT_NAME",
        "PROJECT_CATEGORY",
        "PROJECT_DESCRIPTION",
        "CUSTOMER_ID",
        "BEGIN_DATE",
        "END_DATE",
        "DEPARTMENT_ID",
        "LOCATION_ID",
        "CURRENCY",
        "EVENT_NAME",
        "STATE",
      ];
      const csv = convertToCSV(sageData, columns);
      downloadCSV(csv, "sage projects export.csv");

      toast.showToast({
        title: `Exported ${selected.length} project${selected.length !== 1 ? "s" : ""}`,
        toastType: "success",
      });
    },
    [toast],
  );

  const handleSageExport = useCallback(
    async ({ selectedIds, selectedMap }: any) => {
      const skipConfirmation = await AsyncStorage.getItem("project_sage_export_skip_confirmation");

      if (skipConfirmation === "true") {
        await runSageExport({ selectedIds, selectedMap });
        return;
      }

      openModal(
        <ExportProjectModal
          count={selectedIds.length}
          onConfirm={async (dontShowAgain) => {
            closeModal();
            if (dontShowAgain) {
              await AsyncStorage.setItem("project_sage_export_skip_confirmation", "true");
            }
            await runSageExport({ selectedIds, selectedMap });
          }}
          onCancel={closeModal}
        />,
      );
    },
    [openModal, closeModal, runSageExport],
  );

  const deleteConfirmation: DeleteConfirmationConfig<FromDB<ProjectType>> = useMemo(
    () => ({
      storageKey: "project_delete_skip_confirmation",
      renderModal: ({ count, onConfirm, onCancel }) => (
        <DeleteProjectModal
          count={count}
          onConfirm={async (dontShowAgain) => {
            if (dontShowAgain) {
              await AsyncStorage.setItem("project_delete_skip_confirmation", "true");
            }
            onConfirm();
          }}
          onCancel={onCancel}
        />
      ),
    }),
    [],
  );

  const fetchProjects = useCallback(
    () => projectService.getProjects(showArchived) as Promise<FromDB<ProjectType>[]>,
    [showArchived],
  );

  if (!hasProjectsAccess) {
    return <NoPermissionScreen entityName={displayNames?.projects?.plural || "Projects"} />;
  }

  return (
    <EntityTablePage<FromDB<ProjectType>>
      key={showArchived ? "with-archived" : "without-archived"}
      {...tableConfig}
      fetchData={fetchProjects}
      updateEntities={projectService.saveRowPatches}
      deleteEntities={projectService.deleteProjectsByIds}
      deleteConfirmation={deleteConfirmation}
      showArchived={showArchived}
      onArchiveToggle={setShowArchived}
      // TODO(archive): disabled pending archive guards — decide who/what may be
      // archived before re-enabling. Discuss with team.
      // archiveEntities={projectService.archiveProjects}
      selectionActions={({ selectedIds, selectedMap }) =>
        showArchived
          ? []
          : [
              {
                id: "export-sage",
                label: "Export to Sage",
                icon: "export-variant",
                onPress: () => handleSageExport({ selectedIds, selectedMap }),
              },
            ]
      }
    />
  );
}
